// src/components/events/eventMeta.js
// Справочники и форматирование для деталей событий (мобильный лист и десктопный диалог)
import DeleteIcon from "@mui/icons-material/DeleteForever";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import BuildIcon from "@mui/icons-material/Build";

export const GROUP_TITLES = {
  loss_orders: "Потеря заказа",
  loss_money: "Потеря денег",
  manipulations: "Манипуляции с позициями",
  unusual_actions: "Нестандартные действия",
};

// Акцент категории в деталях событий
export const GROUP_COLORS = {
  loss_orders: "#ff8a50",
  loss_money: "#ef5350",
  manipulations: "#fbc02d",
  unusual_actions: "#ab47bc",
};

// Те же иконки, что на карточках категорий
export const GROUP_ICONS = {
  loss_orders: DeleteIcon,
  loss_money: MoneyOffIcon,
  manipulations: CompareArrowsIcon,
  unusual_actions: BuildIcon,
};

export const EVENT_TYPE_NAMES = {
  orderDeleted: "Удаление заказа",
  orderCancelPrecheque: "Отмена пречека",
  orderReturned: "Сторнирование чека",
  orderPaidNoCash: "Оплата без выручки",
  orderPrepayReturned: "Возврат предоплаты",
  dishesMovedAfterCheque: "Перенос позиций после пречека",
  dishesMoved: "Перенос позиций к другому официанту",
  dishesMovedTo: "Перенос позиций в заказ",
  deletedNewItems: "Удаление неотпечатанных позиций заказа",
  deletedPrintedItems: "Удаление отпечатанных позиций заказа",
  openPriceApplied: "Применение свободной цены",
  serviceChequeRePrinted: "Повторная печать на кухню",
};

// Тип события → группа (как group_map на бэкенде, data_service/services/event_types.py)
export const EVENT_TYPE_GROUPS = {
  orderDeleted: "loss_orders",
  orderCancelPrecheque: "loss_orders",
  orderReturned: "loss_orders",
  orderPaidNoCash: "loss_money",
  orderPrepayReturned: "loss_money",
  dishesMovedAfterCheque: "manipulations",
  dishesMoved: "manipulations",
  dishesMovedTo: "manipulations",
  deletedNewItems: "manipulations",
  deletedPrintedItems: "manipulations",
  openPriceApplied: "unusual_actions",
  serviceChequeRePrinted: "unusual_actions",
};

export const getAttr = (event, name) => event.attributes?.find((a) => a.name === name)?.value;

export const formatValue = (val) => {
  if (!val || val === "-" || val === "0E-9") return "-";
  if (!isNaN(val) && val.toString().includes(".")) return parseFloat(val).toString();
  return val;
};

export const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

// «11:46» для периода в один день, иначе «19.10, 11:46»
export const formatEventTime = (dateString, singleDay) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString || "";
  const time = date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  if (singleDay) return time;
  const day = date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
  return `${day}, ${time}`;
};

// Пустое значение («-», null, пустая строка) → null
export const cleanValue = (raw) => {
  const value = formatValue(raw);
  if (value === "-" || value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
};

// «2 340 ₽» — разделитель разрядов и неразрывный пробел перед ₽; пусто/0 → null
export const formatMoney = (raw) => {
  const value = cleanValue(raw);
  if (value === null) return null;
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  if (number === 0) return null;
  return `${number.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;
};

export const pluralize = (n, one, few, many) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

// Признак списания: реальное имя атрибута — withWriteoff, operationWithWriteoff — запасной вариант
const WRITEOFF_ATTRS = ["withWriteoff", "operationWithWriteoff"];

export const hasWriteoff = (event) => WRITEOFF_ATTRS.some((name) => getAttr(event, name) !== undefined);

// true → «Да», иначе «Нет»; атрибута нет или он пустой → null
export const getWriteoffLabel = (event) => {
  for (const name of WRITEOFF_ATTRS) {
    const value = cleanValue(getAttr(event, name));
    if (value !== null) return value.toLowerCase() === "true" ? "Да" : "Нет";
  }
  return null;
};

// Новые события сверху
export const sortByDateDesc = (events) =>
  [...events].sort((a, b) => new Date(b.date) - new Date(a.date));
